/**
 * Utility functions for helper data conversion
 */

/**
 * Formats helper name for display as "surname, first name"
 */
export const formatHelperDisplayName = (first_name: string | null | undefined, surname: string | null | undefined): string => {
  const first = (first_name || '').trim();
  const last = (surname || '').trim();
  return [last, first].filter(Boolean).join(', ') || '';
};

/**
 * Check if a helper matches a search query. Supports:
 * - "firstname", "surname" (single word)
 * - "firstname surname" (space-separated)
 * - "surname, firstname" (comma-separated)
 * - "surname, firstname" with spaces
 * - email (partial or full)
 */
export const helperMatchesSearch = (
  query: string,
  first_name: string | null | undefined,
  surname: string | null | undefined,
  email?: string | null
): boolean => {
  const q = query.trim();
  if (!q) return true;
  const firstName = (first_name || '').toLowerCase().trim();
  const surnameLower = (surname || '').toLowerCase().trim();
  const emailLower = (email || '').toLowerCase().trim();
  // Split by comma and/or whitespace
  const parts = q.toLowerCase().split(/[\s,]+/).filter(p => p.length > 0);
  if (parts.length === 0) return true;
  return parts.every(part =>
    firstName.includes(part) || surnameLower.includes(part) || emailLower.includes(part)
  );
};

/**
 * Converts bereit_fuer_mehrere_einsaetze from database format (boolean true/false or integer 0/1) 
 * to UI format (string 'Nein'/'Ja')
 * 
 * @param value - The value from the database (can be boolean true/false, integer 0/1, string 'Ja'/'Nein', or null/undefined)
 * @returns 'Ja' if value represents true/willing, 'Nein' otherwise
 */
export const convertBereitFuerMehrereEinsaetze = (value: boolean | number | string | null | undefined): string => {
  if (value === null || value === undefined) {
    return 'Nein';
  }
  
  // Handle boolean values (true = 'Ja', false = 'Nein')
  if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nein';
  }
  
  // Handle integer values (0 or 1)
  if (typeof value === 'number') {
    return value === 1 ? 'Ja' : 'Nein';
  }
  
  // Handle string values
  if (typeof value === 'string') {
    const normalized = value.trim();
    if (normalized === '1' || normalized.toLowerCase() === 'ja' || normalized === 'Ja') {
      return 'Ja';
    }
    return 'Nein';
  }
  
  // Default to 'Nein'
  return 'Nein';
};

/**
 * Converts a helper object's bereit_fuer_mehrere_einsaetze field
 * @param helper - Helper object with bereit_fuer_mehrere_einsaetze field
 * @returns Helper object with converted bereit_fuer_mehrere_einsaetze field
 */
export const convertHelperBereitFuerMehrereEinsaetze = <T extends { bereit_fuer_mehrere_einsaetze?: boolean | number | string | null }>(
  helper: T
): T => {
  return {
    ...helper,
    bereit_fuer_mehrere_einsaetze: convertBereitFuerMehrereEinsaetze(helper.bereit_fuer_mehrere_einsaetze)
  };
};

/** Minimal shape for Fachbereich / Rückmeldung checks (matches API helper + feedback). */
export type HelperFachbereichFeedback = {
  fachbereiche?: string[];
  feedback?: {
    fachbereich: string;
    rückmeldung?: string | null;
    bemerkung?: string | null;
    passtNichtZentralBestaetigtAt?: string | null;
  }[];
};

/**
 * Rückmeldung for one Fachbereich from nested feedback (null if no row).
 */
export const getRueckmeldungForFachbereich = (
  helper: HelperFachbereichFeedback,
  fachbereich: string
): string | null => {
  const fb = helper.feedback?.find(f => f.fachbereich === fachbereich);
  return fb?.rückmeldung ?? null;
};

/** „Passt nicht“ on this Fachbereich with central admin acknowledgement. */
export const isZentralBestaetigtesPasstNichtForFachbereich = (
  helper: HelperFachbereichFeedback,
  fachbereich: string
): boolean => {
  const fb = helper.feedback?.find(f => f.fachbereich === fachbereich);
  return fb?.rückmeldung === 'Passt nicht' && !!fb.passtNichtZentralBestaetigtAt;
};

/** Other Fachbereiche to show in a Fachbereich Helferliste (excludes current FB and zentral bestätigt „Passt nicht“). */
export const getVisibleOtherFachbereichFeedback = (
  helper: HelperFachbereichFeedback,
  currentFachbereich: string
) => {
  return (helper.feedback || [])
    .filter((fb) => {
      if (fb.fachbereich === currentFachbereich) return false;
      if (fb.rückmeldung === 'Passt nicht' && fb.passtNichtZentralBestaetigtAt) return false;
      return true;
    })
    .sort((a, b) => a.fachbereich.localeCompare(b.fachbereich, 'de'));
};

/**
 * True if the helper has at least one assigned Fachbereich whose Rückmeldung is not „Passt nicht“
 * (including no Rückmeldung yet). If every assigned Fachbereich is only „Passt nicht“, returns false —
 * they are treated like helpers without a wirksame Zuordnung for „Ohne Zuordnung“ and admin counts.
 */
export const hasEffectiveFachbereichZuordnung = (helper: HelperFachbereichFeedback): boolean => {
  if (!helper.fachbereiche?.length) return false;
  return helper.fachbereiche.some(fbName => {
    const r = getRueckmeldungForFachbereich(helper, fbName);
    return r !== 'Passt nicht';
  });
};

